import Tabs from "../tabs";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, CardContent, Box, RadioGroup, FormControl, FormControlLabel, Radio, Typography, IconButton, CardMedia } from "@mui/material";
import SingleSelect from "../../../../components/selections/singleSelect";
import CustomButton from "../../../../components/buttons/customButton";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import CloseIcon from "@mui/icons-material/Close";
import { addFileUserLevelThunk, getFileUsersThunk, addFileClientLevelThunk, getFileClinetsThunk } from "../../filesThunk";
import { uploadFileThunk } from "../../../../components/general/generalThunk";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../../config/appRoutes";
import { getContactThunk } from "./../../../contact/contactThunk";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default function FilesCreation() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { getCategories = [] } = useSelector((state) => state.fileCategories || {});
    const { status, error } = useSelector((state) => state.general || {});
    const loading = useSelector((state) => (state.fileCategories && state.fileCategories.status) || false);
    const { getContacts = [] } = useSelector((state) => state.contacts || {});
    const { branchDepartments = [] } = useSelector((state) => state.general || {});

    const [state, setState] = useState({
        selectedValue: "1",
        selectCategoryId: "",
        selectContactUser: "",
        selectBranchDeptId: "",
        errors: {},
        uploadMediaFiles: [],
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });


    useEffect(() => {
        dispatch(getContactThunk());
    }, [dispatch]);

    const { selectCategoryId, selectContactUser, selectBranchDeptId, uploadMediaFiles, selectedValue } = state;

    const handleOptionChange = (event) => {
        setState((prev) => ({
            ...prev,
            selectedValue: event.target.value,
            selectCategoryId: "",
            selectContactUser: "",
            selectBranchDeptId: "",
            errors: {},
            uploadMediaFiles: [],
        }));
    };

    const userLevelFields = [
        ...selectedValue !== "1" ? [
            {
                id: 1,
                name: "selectBranchDeptId",
                value: selectBranchDeptId,
                type: "selection",
                options: Array.isArray(branchDepartments) ? branchDepartments.map((item) => ({ value: item.id, label: item.branch_department_name })) : [],
                label: "Select Department"
            },
        ] : [],
        {
            id: 2,
            name: "selectCategoryId",
            value: selectCategoryId,
            type: "selection",
            options: Array.isArray(getCategories) ? getCategories.map((item) => ({ value: item.id, label: item.category_name })) : [],
            label: "Select Category"
        },
        ...selectedValue !== "1" ? [] : [
            {
                id: 3,
                name: "selectContactUser",
                value: selectContactUser,
                type: "selection",
                label: "Select User",
                options: Array.isArray(getContacts) ? getContacts.map((item) => ({ value: item.id, label: `${item.name}-(${item.mobile})` })) : [],
            },
        ],
        { id: 4, name: "uploadMediaFiles", value: "uploadMediaFiles", type: "media-files" },
    ];


    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const handleFileUpload = async (event) => {
        event.preventDefault();
        if (!event || !event.target || !event.target.files) return;
        const files = Array.from(event.target.files || []);

        if (files.length > 20) {
            alert("You can upload a maximum of 20 files");
            return;
        }
        try {
            const filesData = await Promise.all(files.map(file => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve({ base64Data: reader.result, file });
                reader.onerror = reject;
                reader.readAsDataURL(file);
            })));

            setState((prevState) => ({
                ...prevState,
                uploadMediaFiles: [...prevState.uploadMediaFiles, ...filesData],
                errors: { ...prevState.errors, uploadMediaFiles: "" },
            }));
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const removeFile = (index) => {
        setState((prevState) => {
            const updated = Array.isArray(prevState.uploadMediaFiles) ? [...prevState.uploadMediaFiles] : [];
            updated.splice(index, 1);
            return { ...prevState, uploadMediaFiles: updated };
        });

        setSnackbar({
            open: true,
            message: `This file has been removed`,
            severity: "success",
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Build validations based on selectedValue
        let extraValidations = [];

        // Always validate files
        extraValidations.push({
            valid: Array.isArray(uploadMediaFiles) && uploadMediaFiles.length > 0,
            message: "Upload data is required",
            field: "uploadMediaFiles",
        });

        if (selectedValue === "1") {
            // Category and User
            extraValidations.push(
                { valid: !!selectCategoryId, message: "Category is required", field: "selectCategoryId" },
                { valid: !!selectContactUser, message: "User is required", field: "selectContactUser" }
            );
        } else if (selectedValue === "2") {
            // Branch Dept and Category
            extraValidations.push(
                { valid: !!selectBranchDeptId, message: "Branch Dept is required", field: "selectBranchDeptId" },
                { valid: !!selectCategoryId, message: "Category is required", field: "selectCategoryId" }
            );
        }

        // Filter invalid rules
        const invalidRules = extraValidations.filter(rule => !rule.valid);

        if (invalidRules.length > 0) {
            // Update state for all invalid fields
            const newErrors = invalidRules.reduce((acc, rule) => {
                acc[rule.field] = rule.message;
                return acc;
            }, {});

            setState(prev => ({
                ...prev,
                errors: { ...prev.errors, ...newErrors },
            }));

            // Show snackbar for first error (optional)
            setSnackbar({
                open: true,
                message: invalidRules[0].message,
                severity: "error",
            });

            return; // stop submission
        }


        const file = Array.isArray(uploadMediaFiles)
            ? uploadMediaFiles.map(item => ({
                filename: item?.file?.name || '',
                img: item?.base64Data || '',
            }))
            : [];


        try {

            const uploadResult = await dispatch(
                uploadFileThunk({ credentials: { file, category: "categoryFile" } })
            );

            if (
                uploadResult.meta.requestStatus !== "fulfilled" ||
                !uploadResult.payload?.data?.length
            ) {
                return setState({
                    open: true,
                    message: uploadResult.payload?.message || "File upload failed",
                    severity: "error",
                });
            }

            const fileUrl = Array.isArray(uploadResult?.payload?.data)
                ? uploadResult.payload.data.map((item) => item.file_id).map(([first]) => first).filter((val) => val !== 1)
                : [];

            let result;

            if (selectedValue === "1") {
                result = await dispatch(
                    addFileUserLevelThunk({
                        credentials: {
                            file_categroy_id: selectCategoryId,
                            files: fileUrl.map((item) => ({ "content_mapping_id": item })),
                            wa_contact_id: selectContactUser,
                        },
                    })
                );
            } else {
                result = await dispatch(
                    addFileClientLevelThunk({
                        credentials: {
                            file_categroy_id: selectCategoryId,
                            files: fileUrl.map((item) => ({ "content_mapping_id": item })),
                            branch_department_id: selectBranchDeptId,
                        },
                    })
                );
            }

            const isSuccess = result.meta.requestStatus === "fulfilled";

            setSnackbar({
                open: true,
                message: isSuccess
                    ? result.payload.message
                    : result.payload || "File upload failed",
                severity: isSuccess ? "success" : "error",
            });

            if (isSuccess) {
                if (selectedValue === "1") {
                    await dispatch(getFileUsersThunk({ type: "getUserLevelData" }));
                } else {
                    await dispatch(getFileClinetsThunk({ type: "getClientLevelData" }));
                }
                navigate(APP_ROUTES.FILES.LIST);
            }

        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    useEffect(() => {
        if (error !== null) {
            setState({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [error]);

    return (
        <div>
            <Tabs />

            <Box mt={2}>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="my-radio-group-label"
                        name="my-radio-group"
                        value={selectedValue}
                        onChange={handleOptionChange}
                        row
                    >
                        <FormControlLabel value="1" control={<Radio />} label="User Files" />
                        <FormControlLabel value="2" control={<Radio />} label="Client Files" />
                    </RadioGroup>
                </FormControl>

                <Card>
                    <CardContent>
                        <Box>
                            <Grid container spacing={1}>
                                {
                                    userLevelFields.map((field, index) => (
                                        <Grid item size={{ xs: 12, md: 4 }} key={index}>
                                            {field.type === "media-files" ? (
                                                <Box>
                                                    {/* {uploadMediaFiles.length > 0 && (<Link
                                                        underline="hover"
                                                        sx={{ display: "flex", justifyContent: "flex-end", cursor: "pointer" }}
                                                        onClick={toggleSidebar(true)}
                                                    >
                                                        View File
                                                    </Link>)} */}

                                                    <Box
                                                        component="label"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            flexDirection: "column",
                                                            borderRadius: 1,
                                                            cursor: "pointer",
                                                            width: "100%",
                                                            border: !!state.errors[field.name] ? "1px dashed var(--accent-2)" : "1px dashed var(--active-color)",
                                                            "&:hover": {
                                                                border: !!state.errors[field.name] ? "1px dashed var(--accent-2)" : "1px dashed var(--active-color)",
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex" }}><CloudUploadIcon sx={{ fontSize: 35, color: "#1976d2" }} />&nbsp;&nbsp;<Typography mt={1} variant="body1">Upload files</Typography></Box>
                                                        <VisuallyHiddenInput
                                                            fullWidth
                                                            multiple
                                                            accept=".png, .pdf, image/png, application/pdf"
                                                            type="file"
                                                            onChange={(e) => {
                                                                handleFileUpload(e);
                                                                e.target.value = "";
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            ) : field.type === "selection" ? (
                                                <SingleSelect
                                                    name={field.name}
                                                    label={field.label}
                                                    options={field.options}
                                                    value={field.value}
                                                    onChange={handleSingleSelectChange}
                                                    error={!!state.errors[field.name]}
                                                    formHelperText={false}
                                                />
                                            ) : null}
                                        </Grid>
                                    ))
                                }


                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, maxHeight: "50vh", overflowY: "auto", bgcolor: "background.paper", scrollbarWidth: "thin", mt: 3 }}>
                                    {Array.isArray(state.uploadMediaFiles) && state.uploadMediaFiles.map((item, index) => (
                                        <Card
                                            key={index}
                                            sx={{
                                                mt: 2,
                                                width: 250,
                                                height: 200,
                                                position: "relative",
                                                border: "1px solid #ddd",
                                                boxShadow: 1,
                                            }}
                                        >
                                            {/* Close Button */}
                                            <IconButton
                                                size="small"
                                                onClick={() => removeFile(index)}
                                                sx={{
                                                    position: "absolute",
                                                    textAlign: "center",
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: "var(--btn-1)",
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>

                                            {/* Preview */}
                                            {item?.file?.type?.startsWith("image/") ? (
                                                <CardMedia
                                                    component="img"
                                                    src={item?.base64Data || ''}
                                                    alt={item?.file?.name || ''}
                                                    sx={{ height: 150, objectFit: "cover" }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        height: 120,
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        bgcolor: "#f5f5f5",
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    PDF
                                                </Box>
                                            )}

                                            <CardContent
                                                sx={{
                                                    p: 2,
                                                    textAlign: "center",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <Typography variant="body2" noWrap title={item?.file?.name || ''}>
                                                    {item?.file?.name || ''}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </Grid>

                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <CustomButton
                                    status={status || loading}
                                    onClick={handleSubmit}
                                    label="Add File"
                                    loadingLabel="Submitting.."
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />

        </div>
    );
}
